import { Component, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { EMPTY, catchError, finalize, first, startWith, tap } from 'rxjs';
import { SuperAdminUser } from 'src/app/core/models/platform-access/super-admin-user.interface';
import { AuthService } from 'src/app/core/services/auth.service';
import { PlatformAdminService } from 'src/app/core/services/platform-admin.service';

@Component({
  selector: 'app-admin-management',
  templateUrl: './admin-management.component.html',
  styleUrls: ['./admin-management.component.scss'],
  standalone: false,
})
export class AdminManagementComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly platformAdminService = inject(PlatformAdminService);

  public readonly superAdminEmailControl = new FormControl('', { nonNullable: true });

  public superAdmins: SuperAdminUser[] = [];
  public isLoading = false;
  public isGrantSubmitting = false;
  public canSubmitGrant = false;
  public currentUserId?: string;

  public ngOnInit(): void {
    this.currentUserId = this.authService.user$.getValue().data?.id;
    this.superAdminEmailControl.valueChanges
      .pipe(
        startWith(this.superAdminEmailControl.value),
        tap((value) => {
          this.canSubmitGrant = value.trim().length > 0;
        }),
      )
      .subscribe();

    this.loadAdmins();
  }

  public grantSuperAdmin(): void {
    const email = this.superAdminEmailControl.value.trim();
    if (!email || this.isGrantSubmitting) {
      return;
    }

    this.isGrantSubmitting = true;
    this.platformAdminService
      .grantSuperAdmin(email)
      .pipe(
        first(),
        tap(() => {
          this.superAdminEmailControl.setValue('');
          this.loadAdmins();
        }),
        catchError(() => EMPTY),
        finalize(() => {
          this.isGrantSubmitting = false;
        }),
      )
      .subscribe();
  }

  public revokeSuperAdmin(userId: string): void {
    if (userId === this.currentUserId) {
      return;
    }

    this.platformAdminService
      .revokeSuperAdmin(userId)
      .pipe(
        first(),
        tap(() => {
          this.loadAdmins();
        }),
      )
      .subscribe();
  }

  private loadAdmins(): void {
    this.isLoading = true;
    this.platformAdminService
      .getSuperAdmins()
      .pipe(
        first(),
        tap((superAdmins) => {
          this.superAdmins = superAdmins;
        }),
        catchError(() => {
          this.superAdmins = [];
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }
}
